import { Utils } from '@Util/utils'
import { MultiplierAnimation, MultiplierAnimationResult } from '../../multiplier-animation'
import { tweenPromise } from '@Animation/utils'
import { MI_MULTIPLIER_CORNHOLE_ASSETS, MI_MULTIPLIER_CORNHOLE_TEXTURES } from './asset-preloader'
import {
  MI_MULTIPLIER_SHARED_ATLAS_FRAMES,
  MI_MULTIPLIER_SHARED_TEXTURE_KEYS,
} from '../shared/constants'

const TARGET_POSITIONS = [
  {
    x: -155,
    y: -135,
  },
  {
    x: 0,
    y: -135,
  },
  {
    x: 155,
    y: -135,
  },
  {
    x: -80,
    y: 0,
  },
  {
    x: 80,
    y: 0,
  },
]

export class MichiganCornholeMultiplierAnimation extends MultiplierAnimation {
  background: Phaser.GameObjects.Image
  bag: Phaser.GameObjects.Image
  board: Phaser.GameObjects.Image

  targetMask: Phaser.Display.Masks.GeometryMask
  shineMask: Phaser.Display.Masks.BitmapMask
  targetMaskGraphics: Phaser.GameObjects.Graphics
  shineMaskObject: Phaser.GameObjects.Image

  targetPosition: { x: number; y: number }

  constructor(scene: Phaser.Scene, x: number, y: number, config: MultiplierAnimationResult) {
    super(scene, x, y, config)

    this.targetPosition = TARGET_POSITIONS[this.multiplierValue - 1]

    this.background = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      MI_MULTIPLIER_CORNHOLE_ASSETS.BACKGROUND,
    )
    this.background.setOrigin(0.5, 0.5)
    this.background.setScale(0.7)

    this.board = this.scene.add.image(
      8,
      180,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      MI_MULTIPLIER_CORNHOLE_ASSETS.BOARD,
    )
    this.board.setOrigin(0.5, 0.5)
    this.board.setScale(0.25)

    this.bag = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      MI_MULTIPLIER_CORNHOLE_ASSETS.BAG,
    )
    this.bag.setOrigin(0.5, 0.5)
    this.bag.setVisible(false)

    this.add([this.background, this.board, this.bag])

    this.shineMaskObject = this.scene.add.image(
      250,
      250,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      `${this.multiplierValue}${MI_MULTIPLIER_CORNHOLE_ASSETS.NUMBER_SUFFIX}`,
    )
    this.shineMaskObject.setVisible(false)
    this.shineMask = new Phaser.Display.Masks.BitmapMask(this.scene, this.shineMaskObject, 250, 250)

    this.targetMaskGraphics = this.scene.make.graphics()

    this.targetMask = this.createTargetMask()
  }

  async play() {
    await this.showKickerLogo()
    await this.throwBag()
    await this.showResult()

    await Utils.sleep(1000)
    this.cleanup()

    this.destroy()
    return
  }

  createTargetMask() {
    this.targetMaskGraphics.fillStyle(0xffffff)
    this.targetMaskGraphics.beginPath()

    // Circle Mask
    this.targetMaskGraphics.arc(
      this.targetPosition.x + 250,
      this.targetPosition.y + 179,
      100,
      0,
      Math.PI * 2,
    )

    this.targetMaskGraphics.fillPath()

    return this.targetMaskGraphics.createGeometryMask()
  }

  async showKickerLogo() {
    const heres = this.scene.add.image(
      -500,
      -100,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.HERES,
    )
    heres.setOrigin(0.5, 0.5)

    const the = this.scene.add.image(
      500,
      -25,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.THE,
    )
    the.setOrigin(0.5, 0.5)

    const kicker = this.scene.add.image(
      -250,
      -300,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.KICKER,
    )
    kicker.setOrigin(0.5, 0.5)

    kicker.alpha = 0

    const introDuration = 300

    const introHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 10,
      duration: introDuration,
    })
    const introThe = tweenPromise(this.scene, {
      targets: the,
      x: -10,
      duration: introDuration,
    })
    const introKicker = tweenPromise(this.scene, {
      targets: kicker,
      x: 0,
      y: 50,
      alpha: 1,
      duration: introDuration,
    })

    this.add([kicker, heres, the])

    await Promise.all([introHeres(), introThe(), introKicker()])

    this.zoomPreThrow()

    const driftDuration = 1100
    const driftHeres = tweenPromise(this.scene, {
      targets: heres,
      x: -25,
      duration: driftDuration,
    })

    const driftThe = tweenPromise(this.scene, {
      targets: the,
      x: 25,
      duration: driftDuration,
    })

    await Promise.all([driftHeres(), driftThe()])

    const outroDuration = 300

    const outroHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 500,
      duration: outroDuration,
    })
    const outroThe = tweenPromise(this.scene, {
      targets: the,
      x: -500,
      duration: outroDuration,
    })
    const outroKicker = tweenPromise(this.scene, {
      targets: kicker,
      scale: 0.4,
      y: 200,
      alpha: 0,
      duration: outroDuration,
    })

    await Promise.all([outroHeres(), outroThe(), outroKicker()])

    heres.destroy()
    the.destroy()
    kicker.destroy()
  }

  async zoomPreThrow() {
    const duration = 1500

    const scaleBG = tweenPromise(this.scene, {
      targets: this.background,
      y: -500,
      scale: 2,
      duration: duration,
    })

    const scaleBoard = tweenPromise(this.scene, {
      targets: this.board,
      y: 35,
      scale: 0.65,
      duration: duration,
    })

    await Promise.all([scaleBG(), scaleBoard()])
  }

  async zoomMidThrow() {
    const duration = 2000

    const scaleBG = tweenPromise(this.scene, {
      targets: this.background,
      y: -750,
      scale: 3,
      duration: duration,
    })

    const scaleBoard = tweenPromise(this.scene, {
      targets: this.board,
      y: 225,
      scale: 2,
      duration: duration,
    })

    await Promise.all([scaleBG(), scaleBoard()])
  }

  async throwBag() {
    this.bag.setPosition(150, 300)
    this.bag.setVisible(true)

    await tweenPromise(this.scene, {
      targets: this.bag,
      y: 250,
      duration: 500,
    })()

    await tweenPromise(this.scene, {
      targets: this.bag,
      y: 270,
      duration: 400,
      yoyo: true,
      loop: 1,
    })()

    await tweenPromise(this.scene, {
      targets: this.bag,
      y: 300,
      duration: 400,
    })()

    this.zoomMidThrow()

    // Bag Up
    await tweenPromise(this.scene, {
      targets: this.bag,
      y: -400,
      rotation: 4 * Math.PI,
      duration: 1000,
    })()

    // Bag Down
    await tweenPromise(this.scene, {
      targets: this.bag,
      x: TARGET_POSITIONS[this.multiplierValue - 1].x,
      y: TARGET_POSITIONS[this.multiplierValue - 1].y + 100,
      scale: 0.8,
      rotation: 2 * Math.PI,
      duration: 500,
    })()

    // Bag Slide
    await tweenPromise(this.scene, {
      targets: this.bag,
      x: TARGET_POSITIONS[this.multiplierValue - 1].x,
      y: TARGET_POSITIONS[this.multiplierValue - 1].y - 15,
      rotation: 0.5 * Math.PI,
      duration: 500,
    })()

    this.bag.setMask(this.targetMask)

    //Bag Drop
    await tweenPromise(this.scene, {
      targets: this.bag,
      y: TARGET_POSITIONS[this.multiplierValue - 1].y + 100,
      scale: 0.2,
      alpha: 0,
      rotation: Math.PI,
      duration: 350,
    })()
  }

  async showResult() {
    const numberResult = this.scene.add.image(
      this.targetPosition.x,
      this.targetPosition.y + 50,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      `${this.multiplierValue}${MI_MULTIPLIER_CORNHOLE_ASSETS.NUMBER_SUFFIX}`,
    )

    const standardScale = 0.2
    const offSetFactor = this.targetPosition.x / 155
    numberResult.setScale(standardScale * (1 + Math.abs(offSetFactor)), standardScale)
    numberResult.setRotation(-offSetFactor * Utils.degreesToRadians(5))
    numberResult.setOrigin(0.5, 0.5)

    const boardBlank = this.scene.add.image(
      8,
      200,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      MI_MULTIPLIER_CORNHOLE_ASSETS.BOARD_BLANK,
    )
    boardBlank.setPosition(this.board.x, this.board.y)
    boardBlank.setScale(this.board.scale)
    this.add(boardBlank)
    this.moveBelow(boardBlank, this.board)

    this.add(numberResult)
    const centerNumber = tweenPromise(this.scene, {
      targets: numberResult,
      scale: 1,
      x: 0,
      y: 0,
      rotation: 0,
      duration: 500,
    })

    const fadeBoard = tweenPromise(this.scene, {
      targets: this.board,
      alpha: 0,
      duration: 400,
    })

    await Promise.all([centerNumber(), fadeBoard()])

    const shine = this.scene.add.image(
      numberResult.x - 500,
      numberResult.y,
      MI_MULTIPLIER_CORNHOLE_TEXTURES.ATLAS,
      MI_MULTIPLIER_CORNHOLE_ASSETS.FLASH,
    )
    shine.setOrigin(0.5, 0.5)

    this.shineMaskObject.setScale(numberResult.scale)
    shine.setMask(this.shineMask)

    this.add(shine)

    await Utils.sleep(200)

    await tweenPromise(this.scene, {
      targets: shine,
      x: numberResult.x + 500,
      duration: 800,
    })()

    shine.destroy()
  }

  destroy(fromScene?: boolean): void {
    this.shineMaskObject.destroy()
    this.targetMaskGraphics.destroy()
    super.destroy(fromScene)
  }
}
